import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Users, Target, Lightbulb, Globe, Heart, 
  Layers, Cpu, ShoppingBag, ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';

const AboutUs = () => {
  return (
    <MarketingLayout>
      <Helmet>
        <title>About Us | NanoKart.ai - AI Virtual Try-On for Fashion</title>
        <meta name="description" content="Learn about NanoKart.ai - A team of engineers, fashion designers, and product thinkers building AI-powered virtual try-on for Indian fashion commerce." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 sm:pt-28 sm:pb-24 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-6xl mx-auto">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 border border-blue-400/30 rounded-full text-blue-300 text-sm mb-6">
                <Users className="w-4 h-4" />
                <span className="font-medium">About NanoKart.ai</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Reimagining How India{" "}
                <span className="text-blue-400">Shops for Fashion</span>
              </h1>
              
              <p className="text-lg text-slate-300 leading-relaxed">
                A team of engineers, fashion designers, and product thinkers with decades of combined industry experience, building the future of fashion commerce.
              </p>
            </motion.div>

            {/* Right - Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-blue-500/20">
                <img 
                  src="https://v1-garments.s3.ap-south-1.amazonaws.com/nanokart_ai/20260125090848.webp"
                  alt="NanoKart.ai Team - Building AI-powered virtual try-on for fashion"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
              </div>
              
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Cpu className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Purpose-Built AI</p>
                    <p className="font-semibold text-slate-800">For Indian Fashion</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-full text-blue-700 text-sm mb-4">
                <Users className="w-4 h-4" />
                <span className="font-medium">Who We Are</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Deep Expertise, One Mission
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
                We bring together deep expertise in applied AI, large-scale systems, and fashion commerce to reimagine how people shop for fashion.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { icon: Cpu, title: 'Applied AI', desc: 'Deep learning and computer vision expertise' },
                { icon: Layers, title: 'Large-Scale Systems', desc: 'Production-grade infrastructure' },
                { icon: ShoppingBag, title: 'Fashion Commerce', desc: 'Industry insight and domain knowledge' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-slate-50 rounded-2xl p-6 text-center hover:shadow-md transition-shadow"
                >
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500/20 border border-red-400/30 rounded-full text-red-300 text-sm mb-4">
                <Target className="w-4 h-4" />
                <span className="font-medium">The Problem We're Solving</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6">
                Fashion Ecommerce Is Broken
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: 'Static Photos', desc: 'Fashion ecommerce still relies on static photos and guesswork.' },
                { title: 'Low Confidence', desc: 'Customers lack confidence in how clothes will look on them.' },
                { title: 'High Returns', desc: 'Sellers face unsustainably high return rates.' },
                { title: 'Generic Solutions', desc: 'Existing virtual try-on solutions fail to handle real-world Indian dresses, drapes, and body diversity.' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-5"
                >
                  <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-400">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-full text-emerald-700 text-sm mb-4">
                  <Lightbulb className="w-4 h-4" />
                  <span className="font-medium">Our Approach</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                  Built From the Ground Up for Indian Fashion
                </h2>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  We built a dedicated AI virtual try-on system from the ground up, designed for complex dresses, natural draping, and diverse body types.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Unlike generic, non-India-trained models, our solution is purpose-built for real Indian fashion commerce.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="grid grid-cols-2 gap-4"
              >
                {[
                  { label: 'Complex Dresses', color: 'bg-blue-100 text-blue-700' },
                  { label: 'Natural Draping', color: 'bg-purple-100 text-purple-700' },
                  { label: 'Diverse Body Types', color: 'bg-emerald-100 text-emerald-700' },
                  { label: 'Indian Fashion', color: 'bg-amber-100 text-amber-700' }
                ].map((item, idx) => (
                  <div key={idx} className={`${item.color} rounded-xl p-5 text-center font-semibold`}>
                    {item.label}
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Inclusive by Design */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-pink-100 border border-pink-200 rounded-full text-pink-700 text-sm mb-4">
                <Heart className="w-4 h-4" />
                <span className="font-medium">Inclusive by Design</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
                Built for Everyone Who Sells Fashion
              </h2>
              <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
                From large marketplaces and premium brands to social sellers and offline boutiques. One platform. The same high-confidence shopping experience, everywhere.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-4 gap-4">
              {[
                { emoji: '🏢', label: 'Marketplaces' },
                { emoji: '👗', label: 'Premium Brands' },
                { emoji: '📱', label: 'Social Sellers' },
                { emoji: '🏬', label: 'Offline Boutiques' }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="text-3xl mb-2 block">{item.emoji}</span>
                  <p className="font-semibold text-slate-800">{item.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 border border-blue-400/30 rounded-full text-blue-300 text-sm mb-6">
                <Globe className="w-4 h-4" />
                <span className="font-medium">Our Vision</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight">
                India Leading the World in Fashion Commerce
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-10">
                To help India lead the world in redefining the online and omnichannel shopping experience, powered by applied AI, built for scale, and inclusive by default.
              </p>
              
              <Link 
                to="/partners/register"
                className="inline-flex items-center gap-2 px-6 py-3.5 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
              >
                Join Our Journey
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default AboutUs;
